import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Building2, 
  Users, 
  DollarSign, 
  Calendar,
  TrendingUp,
  Target,
  Send,
  Mail,
  Phone,
  Plus,
  Search,
  Filter,
  ArrowRight,
  CheckCircle,
  Clock,
  AlertCircle,
  ChevronRight,
  BarChart3,
  Briefcase,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PIPELINE_STAGES = [
  { value: 'New', label: 'New Leads', color: 'bg-slate-100 text-slate-700', icon: Target },
  { value: 'Outreach Sent', label: 'Outreach Sent', color: 'bg-blue-100 text-blue-700', icon: Send },
  { value: 'Contacted', label: 'In Contact', color: 'bg-purple-100 text-purple-700', icon: Phone },
  { value: 'Confirmed', label: 'Confirmed', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  { value: 'Completed', label: 'Completed', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
];

export default function CorporateOutreach() {
  const [partners, setPartners] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailTemplates, setEmailTemplates] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [stageFilter, setStageFilter] = useState('All');
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [partnersRes, statsRes, templatesRes] = await Promise.all([
        fetch(`${API}/corporate/partners`, { credentials: 'include' }),
        fetch(`${API}/corporate/stats`, { credentials: 'include' }),
        fetch(`${API}/corporate/email-templates`, { credentials: 'include' })
      ]);

      if (partnersRes.ok) {
        const data = await partnersRes.json();
        setPartners(data);
      }
      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }
      if (templatesRes.ok) {
        const data = await templatesRes.json();
        setEmailTemplates(data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load outreach data');
    } finally {
      setLoading(false);
    }
  };

  const openEmailDialog = (partner, templateId = 'corporate_outreach') => {
    setSelectedPartner(partner);
    setSelectedTemplate(templateId);
    
    const template = emailTemplates[templateId];
    if (template) {
      const subject = template.subject
        .replace('{company_name}', partner.company_name)
        .replace('{contact_name}', partner.contact_name);
      const body = template.body
        .replace(/{company_name}/g, partner.company_name)
        .replace(/{contact_name}/g, partner.contact_name)
        .replace(/{sender_name}/g, 'The Bookshelf Project Team');
      
      setEmailSubject(subject);
      setEmailBody(body);
    }
    setShowEmailDialog(true);
  };

  const handleTemplateChange = (templateId) => {
    setSelectedTemplate(templateId);
    const template = emailTemplates[templateId];
    if (template && selectedPartner) {
      const subject = template.subject
        .replace('{company_name}', selectedPartner.company_name)
        .replace('{contact_name}', selectedPartner.contact_name);
      const body = template.body
        .replace(/{company_name}/g, selectedPartner.company_name)
        .replace(/{contact_name}/g, selectedPartner.contact_name)
        .replace(/{sender_name}/g, 'The Bookshelf Project Team');
      
      setEmailSubject(subject);
      setEmailBody(body);
    }
  };

  const sendEmail = async () => {
    if (!selectedPartner || !emailSubject || !emailBody) return;
    
    setSendingEmail(true);
    try {
      const response = await fetch(`${API}/corporate/send-custom-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          corporate_id: selectedPartner.corporate_id,
          to_email: selectedPartner.contact_email,
          subject: emailSubject,
          body: emailBody
        })
      });

      if (response.ok) {
        toast.success(`Email sent to ${selectedPartner.contact_email}`);
        setShowEmailDialog(false);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send email');
      }
    } catch (error) {
      toast.error('Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      });
    } catch {
      return dateStr;
    }
  };

  const getStatusBadge = (status) => {
    const stage = PIPELINE_STAGES.find(s => s.value === status);
    if (!stage) return <Badge variant="outline">{status}</Badge>;
    return <Badge className={stage.color}>{status}</Badge>;
  };

  const filteredPartners = partners.filter(p => {
    const matchesSearch = 
      p.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.contact_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.contact_email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStage = stageFilter === 'All' || p.status === stageFilter;
    return matchesSearch && matchesStage;
  });

  // Group partners by pipeline stage
  const pipelineData = PIPELINE_STAGES.map(stage => ({
    ...stage,
    partners: partners.filter(p => p.status === stage.value),
    count: partners.filter(p => p.status === stage.value).length
  }));

  // Calculate outreach metrics
  const outreachMetrics = {
    totalCompanies: partners.length,
    contacted: partners.filter(p => ['Outreach Sent', 'Contacted', 'Confirmed', 'Completed'].includes(p.status)).length,
    confirmed: partners.filter(p => ['Confirmed', 'Completed'].includes(p.status)).length,
    conversionRate: partners.length > 0 
      ? Math.round((partners.filter(p => ['Confirmed', 'Completed'].includes(p.status)).length / partners.length) * 100)
      : 0,
    totalVolunteers: partners.reduce((sum, p) => sum + (p.estimated_volunteers || 0), 0),
    totalSponsorship: partners.reduce((sum, p) => sum + (p.sponsorship_amount || 0), 0)
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-[#5cc8e8] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="corporate-outreach-dashboard">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-[#1a3a5c]" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Corporate Outreach
            </h1>
            <p className="text-slate-500 mt-1">Track partnerships and outreach activities</p>
          </div>
          <Link to="/admin/corporate">
            <Button className="bg-[#1a3a5c] hover:bg-[#1a3a5c]/90" data-testid="manage-partners-btn">
              <Building2 className="w-4 h-4 mr-2" />
              Manage Partners
            </Button>
          </Link>
        </div>

        {/* Outreach Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <Card className="border-0 shadow-md">
            <CardContent className="p-4 text-center">
              <Target className="w-6 h-6 mx-auto mb-2 text-slate-400" />
              <p className="text-2xl font-bold text-[#1a3a5c]">{outreachMetrics.totalCompanies}</p>
              <p className="text-xs text-slate-500">Total Companies</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-md">
            <CardContent className="p-4 text-center">
              <Send className="w-6 h-6 mx-auto mb-2 text-blue-400" />
              <p className="text-2xl font-bold text-[#1a3a5c]">{outreachMetrics.contacted}</p>
              <p className="text-xs text-slate-500">Contacted</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-md">
            <CardContent className="p-4 text-center">
              <CheckCircle className="w-6 h-6 mx-auto mb-2 text-green-400" />
              <p className="text-2xl font-bold text-[#1a3a5c]">{outreachMetrics.confirmed}</p>
              <p className="text-xs text-slate-500">Confirmed</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-md">
            <CardContent className="p-4 text-center">
              <TrendingUp className="w-6 h-6 mx-auto mb-2 text-purple-400" />
              <p className="text-2xl font-bold text-[#1a3a5c]">{outreachMetrics.conversionRate}%</p>
              <p className="text-xs text-slate-500">Conversion Rate</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-md">
            <CardContent className="p-4 text-center">
              <Users className="w-6 h-6 mx-auto mb-2 text-amber-400" />
              <p className="text-2xl font-bold text-[#1a3a5c]">{outreachMetrics.totalVolunteers}</p>
              <p className="text-xs text-slate-500">Est. Volunteers</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-md">
            <CardContent className="p-4 text-center">
              <DollarSign className="w-6 h-6 mx-auto mb-2 text-emerald-400" />
              <p className="text-2xl font-bold text-[#1a3a5c]">${outreachMetrics.totalSponsorship.toLocaleString()}</p>
              <p className="text-xs text-slate-500">Sponsorship</p>
            </CardContent>
          </Card>
        </div>

        {/* Pipeline Overview */}
        <Card className="border-0 shadow-md">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-[#1a3a5c] flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Partnership Pipeline
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex overflow-x-auto gap-4 pb-2">
              {pipelineData.map((stage, idx) => (
                <div 
                  key={stage.value}
                  className="flex-shrink-0 w-48 p-4 bg-slate-50 rounded-xl"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <stage.icon className="w-4 h-4 text-slate-500" />
                      <span className="text-sm font-medium text-slate-700">{stage.label}</span>
                    </div>
                    <Badge className={stage.color}>{stage.count}</Badge>
                  </div>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {stage.partners.slice(0, 5).map(partner => (
                      <div 
                        key={partner.corporate_id}
                        className="p-2 bg-white rounded border text-xs"
                      >
                        <p className="font-medium truncate">{partner.company_name}</p>
                        <p className="text-slate-500 truncate">{partner.contact_name}</p>
                      </div>
                    ))}
                    {stage.count > 5 && (
                      <p className="text-xs text-center text-slate-400">+{stage.count - 5} more</p>
                    )}
                    {stage.count === 0 && (
                      <p className="text-xs text-center text-slate-400 py-4">No companies</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Companies Table */}
        <Card className="border-0 shadow-md">
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <CardTitle className="text-lg font-semibold text-[#1a3a5c] flex items-center gap-2">
                <Briefcase className="w-5 h-5" />
                Company List
              </CardTitle>
              <div className="flex gap-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search companies..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9 w-64"
                    data-testid="search-companies"
                  />
                </div>
                <Select value={stageFilter} onValueChange={setStageFilter}>
                  <SelectTrigger className="w-40" data-testid="stage-filter">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="All">All Stages</SelectItem>
                    {PIPELINE_STAGES.map(stage => (
                      <SelectItem key={stage.value} value={stage.value}>{stage.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {filteredPartners.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Company</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Stage</TableHead>
                    <TableHead>Est. Volunteers</TableHead>
                    <TableHead>Outreach Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPartners.map(partner => (
                    <TableRow key={partner.corporate_id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {partner.company_logo && (
                            <img 
                              src={partner.company_logo} 
                              alt={partner.company_name}
                              className="w-8 h-8 object-contain rounded"
                            />
                          )}
                          <div>
                            <p className="font-medium">{partner.company_name}</p>
                            {partner.industry && (
                              <p className="text-xs text-slate-500">{partner.industry}</p>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm">{partner.contact_name}</p>
                        <p className="text-xs text-slate-500">{partner.contact_email}</p>
                      </TableCell>
                      <TableCell>{getStatusBadge(partner.status)}</TableCell>
                      <TableCell>{partner.estimated_volunteers || 'N/A'}</TableCell>
                      <TableCell>{formatDate(partner.outreach_sent_date)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEmailDialog(partner)}
                            data-testid={`email-${partner.corporate_id}`}
                          >
                            <Mail className="w-4 h-4" />
                          </Button>
                          <Link to="/admin/corporate">
                            <Button variant="ghost" size="sm">
                              <Eye className="w-4 h-4" />
                            </Button>
                          </Link>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-12 text-slate-500">
                <Building2 className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No companies found matching your criteria.</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Email Dialog */}
        <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-[#1a3a5c]" />
                Send Outreach Email
              </DialogTitle>
            </DialogHeader>
            {selectedPartner && (
              <div className="space-y-4">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm">
                    <strong>To:</strong> {selectedPartner.contact_name} &lt;{selectedPartner.contact_email}&gt;
                  </p>
                  <p className="text-sm text-slate-500">
                    <strong>Company:</strong> {selectedPartner.company_name}
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label>Email Template</Label>
                  <Select value={selectedTemplate} onValueChange={handleTemplateChange}>
                    <SelectTrigger data-testid="template-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(emailTemplates).map(([id, template]) => (
                        <SelectItem key={id} value={id}>{template.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Subject</Label>
                  <Input
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    data-testid="email-subject"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Email Body</Label>
                  <Textarea
                    value={emailBody}
                    onChange={(e) => setEmailBody(e.target.value)}
                    rows={12}
                    className="font-mono text-sm"
                    data-testid="email-body"
                  />
                </div>
                
                <div className="flex gap-2 pt-2">
                  <Button 
                    variant="outline" 
                    onClick={() => setShowEmailDialog(false)} 
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={sendEmail}
                    disabled={sendingEmail || !emailSubject || !emailBody}
                    className="flex-1 bg-[#1a3a5c] hover:bg-[#1a3a5c]/90"
                    data-testid="send-email-btn"
                  >
                    {sendingEmail ? 'Sending...' : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Email
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
